import { WORLD, SNACKS, MACHINE_TYPES, POWERUPS, MATCH, EVENTS, GADGETS, PETS, TRAILS, AURAS, SKINS, SKIN_TRAILS } from './config';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);
const dist2 = (ax, ay, bx, by) => { const dx = ax - bx, dy = ay - by; return dx * dx + dy * dy; };

function circleRect(cx, cy, r, rx, ry, rw, rh) {
  const nx = clamp(cx, rx, rx + rw);
  const ny = clamp(cy, ry, ry + rh);
  const dx = cx - nx, dy = cy - ny;
  return dx * dx + dy * dy < r * r ? { nx, ny, dx, dy } : null;
}

function segRect(x1, y1, x2, y2, r) {
  // does segment cross axis-aligned rect r{x,y,w,h}?
  const minX = r.x, maxX = r.x + r.w, minY = r.y, maxY = r.y + r.h;
  // quick reject
  if (Math.max(x1, x2) < minX || Math.min(x1, x2) > maxX ||
      Math.max(y1, y2) < minY || Math.min(y1, y2) > maxY) return false;
  const edges = [[minX, minY, maxX, minY], [maxX, minY, maxX, maxY],
                 [maxX, maxY, minX, maxY], [minX, maxY, minX, minY]];
  for (const [ex1, ey1, ex2, ey2] of edges) {
    if (segSeg(x1, y1, x2, y2, ex1, ey1, ex2, ey2)) return true;
  }
  // endpoint inside
  if (x1 > minX && x1 < maxX && y1 > minY && y1 < maxY) return true;
  return false;
}
function segSeg(a, b, c, d, p, q, r2, s) {
  const d1 = (r2 - p) * (b - q) - (s - q) * (a - p);
  const d2 = (r2 - p) * (d - q) - (s - q) * (c - p);
  const d3 = (c - a) * (q - b) - (d - b) * (p - a);
  const d4 = (c - a) * (s - b) - (d - b) * (r2 - a);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

export class GameEngine {
  constructor(canvas, opts) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.profile = opts.profile;
    this.settings = opts.settings;
    this.net = opts.net;
    this.audio = opts.audio;
    this.isMobile = opts.isMobile;
    this.onHud = opts.onHud || (() => {});
    this.onResults = opts.onResults || (() => {});

    this.keys = {};
    this.mouse = { x: 0, y: 0, worldX: 0, worldY: 0 };
    this.joy = { x: 0, y: 0 };
    this.mobileSprint = false;
    this.remote = [];
    this.remoteCount = 0;

    this.running = false;
    this._raf = null;
    this._lastT = 0;
    this._netT = 0;
    this._hudT = 0;
    this.message = null;
    this.messageT = 0;
    this.matchId = -1;
    this.onMatchEnd = opts.onMatchEnd || (() => {});
    this.shakeT = 0;
    this.shakeMag = 0;

    this._bound = {};
  }

  // ---------- lifecycle ----------
  start() {
    this._resetMatch();
    this._bindInput();
    if (this.net) {
      this.net.onSnapshot = (players, count) => { this.remote = players; this.remoteCount = count; };
      this.net.connect({
        name: this.profile.name, skin: this.profile.color, hat: this.profile.hat,
        x: this.player.x, y: this.player.y,
      });
    }
    this.running = true;
    this._lastT = performance.now();
    this._loop = this._loop.bind(this);
    this._raf = requestAnimationFrame(this._loop);
    if (this.settings.music) this.audio.startMusic();
  }

  stop() {
    this.running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._unbindInput();
    this.audio.stopMusic();
    if (this.net) this.net.onSnapshot = null;
  }

  // ---------- world ----------
  _initWorld() {
    // buildings / obstacles (also block line-of-sight & cover)
    this.buildings = [
      { x: 120, y: 120, w: 360, h: 220, kind: 'shop', label: 'MART' },
      { x: WORLD.w - 500, y: 130, w: 380, h: 240, kind: 'shop', label: 'DINER' },
      { x: 150, y: WORLD.h - 360, w: 330, h: 240, kind: 'shop', label: 'ARCADE' },
      { x: WORLD.w - 470, y: WORLD.h - 380, w: 360, h: 260, kind: 'shop', label: 'CAFE' },
      { x: WORLD.w / 2 - 170, y: WORLD.h / 2 - 120, w: 340, h: 240, kind: 'plaza', label: 'PLAZA' },
      { x: 620, y: 560, w: 150, h: 320, kind: 'block' },
      { x: WORLD.w - 760, y: 520, w: 160, h: 300, kind: 'block' },
      { x: 900, y: WORLD.h - 520, w: 300, h: 150, kind: 'block' },
      { x: WORLD.w - 1150, y: WORLD.h - 500, w: 280, h: 150, kind: 'block' },
    ];

    // decorations (benches, trash, signs) — small collidable/deco
    this.deco = [];
    const decoKinds = ['bench', 'trash', 'sign', 'plant', 'cone'];
    for (let i = 0; i < 34; i++) {
      this.deco.push({
        x: rand(80, WORLD.w - 80), y: rand(80, WORLD.h - 80),
        kind: decoKinds[Math.floor(Math.random() * decoKinds.length)],
        r: rand(0, Math.PI * 2),
      });
    }

    // vending machines around the map
    const spots = [
      [340, 470, 'weak'], [WORLD.w - 340, 470, 'medium'], [340, WORLD.h - 220, 'medium'],
      [WORLD.w - 320, WORLD.h - 230, 'weak'], [WORLD.w / 2, 340, 'strong'],
      [WORLD.w / 2, WORLD.h - 300, 'strong'], [820, WORLD.h / 2, 'medium'],
      [WORLD.w - 820, WORLD.h / 2, 'weak'], [WORLD.w / 2 - 460, WORLD.h / 2 + 60, 'medium'],
    ];
    this.machines = spots.map(([x, y, type], i) => this._makeMachine(i, x, y, type));

    // power-up pads
    const powTypes = Object.keys(POWERUPS);
    const powSpots = [[560, 300], [WORLD.w - 560, 320], [560, WORLD.h - 320],
                      [WORLD.w - 560, WORLD.h - 320], [WORLD.w / 2, WORLD.h / 2 + 10],
                      [WORLD.w / 2 - 300, 620], [WORLD.w / 2 + 300, WORLD.h - 620]];
    this.powerups = powSpots.map(([x, y], i) => ({
      x, y, type: powTypes[i % powTypes.length], active: true, respawn: 0, bob: rand(0, 6),
    }));

    // guards
    this.guards = [
      this._makeGuard(500, 500, [[500, 500], [WORLD.w - 500, 500], [WORLD.w - 500, WORLD.h - 500], [500, WORLD.h - 500]]),
      this._makeGuard(WORLD.w / 2, 500, [[WORLD.w / 2, 400], [WORLD.w / 2, WORLD.h - 400]]),
      this._makeGuard(900, WORLD.h / 2, [[700, WORLD.h / 2], [WORLD.w - 700, WORLD.h / 2]]),
      this._makeGuard(WORLD.w - 600, WORLD.h - 600, [[WORLD.w - 600, 600], [WORLD.w - 600, WORLD.h - 600], [700, WORLD.h - 600]]),
    ];

    this.snacks = [];
    this.particles = [];
    this.noises = [];
    this.floaters = [];

    this.spawns = [[250, WORLD.h / 2], [WORLD.w - 250, WORLD.h / 2], [WORLD.w / 2, 250], [WORLD.w / 2, WORLD.h - 250]];

    this.player = {
      x: WORLD.w / 2, y: WORLD.h - 250, vx: 0, vy: 0, r: 18, angle: -Math.PI / 2,
      score: 0, snacks: 0, thefts: 0, caught: 0, bestSmash: 0,
      pow: {}, attackHeat: 0, actionCd: 0, buyCd: 0, dodgeCd: 0,
      stun: 0, dodging: 0, dodgeVx: 0, dodgeVy: 0, hitFlash: 0, recentSnacks: [],
      combo: 0, comboT: 0, comboBest: 0, gadgetCd: 0, throwCd: 0, swing: 0,
      stamina: 100, exhausted: false, iframe: 0,
    };
    this.peels = [];
    this.projectiles = [];
    this.trailPts = [];
    this.decoy = null;
    this.event = null;
    this.eventCd = 16;
    this.pet = (this.profile.pet && this.profile.pet !== 'none')
      ? { id: this.profile.pet, x: this.player.x, y: this.player.y, bob: 0, t: 3 } : null;
  }

  _makeMachine(id, x, y, type) {
    const t = MACHINE_TYPES[type];
    return { id, x, y, type, w: 70, h: 96, health: t.maxHealth, maxHealth: t.maxHealth,
             shakeX: 0, shakeY: 0, shakeVx: 0, shakeVy: 0, broken: false, respawn: 0, hitCount: 0, flash: 0,
             sway: rand(0, 6.28), doorOpen: 0 };
  }

  _makeGuard(x, y, path) {
    return { x, y, r: 20, angle: 0, path, wp: 0, speed: 150, alert: 0,
             lastX: x, lastY: y, chasing: false, seenT: 0, state: 'patrol', investT: 0,
             fov: Math.PI * 0.58, range: 360, stunT: 0 };
  }

  _resetMatch() {
    this._initWorld();
    this.phase = 'countdown';
    this.phaseT = MATCH.countdown;
    this.timeLeft = MATCH.duration;
    this.resultsT = 8;
    this._lastTick = 99;
    this.results = null;
    this._awarded = false;
    this.boss = null;
    this.bossSpawned = false;
  }

  _syncPhase(dt) {
    const net = this.net;
    const synced = net && net.connected && net.serverPhase;
    let target = this.phase;
    if (synced) {
      if (net.matchId != null && net.matchId !== this.matchId) {
        this.matchId = net.matchId;
        this._resetMatch();
      }
      target = net.serverPhase;
      if (target === 'gameplay') this.timeLeft = net.serverTimeLeft;
      else if (target === 'countdown') { this.phaseT = net.serverTimeLeft; this.timeLeft = MATCH.duration; }
      else if (target === 'results') this.resultsT = net.serverTimeLeft;
    } else {
      if (this.phase === 'countdown') { this.phaseT -= dt; if (this.phaseT <= 0) target = 'gameplay'; }
      else if (this.phase === 'gameplay') { this.timeLeft -= dt; if (this.timeLeft <= 0) { this.timeLeft = 0; target = 'results'; } }
      else if (this.phase === 'results') { this.resultsT -= dt; if (this.resultsT <= 0) { this._resetMatch(); target = 'countdown'; } }
    }
    if (target === 'countdown') {
      const n = Math.ceil(this.phaseT);
      if (n !== this._lastTick) { this._lastTick = n; if (n > 0) this.audio.play('tick'); }
    }
    if (target !== this.phase) {
      if (this.phase === 'countdown' && target === 'gameplay') this.audio.play('go');
      if (target === 'results') this._endMatch();
      this.phase = target;
    }
  }

  // ---------- input ----------
  _bindInput() {
    const kd = (e) => {
      const wasDown = this.keys[e.code];
      this.keys[e.code] = true;
      if ([ 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space' ].includes(e.code)) e.preventDefault();
      if (e.code === 'Space') this._dodge();
      // double-tap a movement key to dash in that direction
      const dirs = { KeyW: [0, -1], KeyS: [0, 1], KeyA: [-1, 0], KeyD: [1, 0],
                     ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
      if (dirs[e.code] && !wasDown) {
        const now = performance.now();
        this._tap = this._tap || {};
        if (this._tap[e.code] && now - this._tap[e.code] < 280) { this._dodge(dirs[e.code][0], dirs[e.code][1]); this._tap[e.code] = 0; }
        else this._tap[e.code] = now;
      }
      if (e.code === 'KeyE') this._interact();
      if (e.code === 'KeyQ') this._useGadget();
      if (e.code === 'KeyF') this._throwSnack();
      this.audio.resume();
    };
    const ku = (e) => { this.keys[e.code] = false; };
    const bl = () => { this.keys = {}; };
    window.addEventListener('blur', bl);
    const mm = (e) => {
      const r = this.canvas.getBoundingClientRect();
      this.mouse.x = e.clientX - r.left; this.mouse.y = e.clientY - r.top;
    };
    const md = (e) => {
      this.audio.resume();
      if (this.isMobile) return;
      if (e.button === 0) this._attack('punch');
      else if (e.button === 2) this._attack('kick');
    };
    const cm = (e) => e.preventDefault();
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    this.canvas.addEventListener('mousemove', mm);
    this.canvas.addEventListener('mousedown', md);
    this.canvas.addEventListener('contextmenu', cm);
    this._bound = { kd, ku, mm, md, cm, bl };
  }

  _unbindInput() {
    const b = this._bound;
    window.removeEventListener('keydown', b.kd);
    window.removeEventListener('keyup', b.ku);
    window.removeEventListener('blur', b.bl);
    this.canvas.removeEventListener('mousemove', b.mm);
    this.canvas.removeEventListener('mousedown', b.md);
    this.canvas.removeEventListener('contextmenu', b.cm);
  }

  // mobile control hooks
  setJoystick(x, y) { this.joy.x = x; this.joy.y = y; }
  setSprint(v) { this.mobileSprint = v; }
  action(name) {
    this.audio.resume();
    if (name === 'punch') this._attack('punch');
    else if (name === 'kick') this._attack('kick');
    else if (name === 'interact') this._interact();
    else if (name === 'dodge') this._dodge();
    else if (name === 'gadget') this._useGadget();
    else if (name === 'throw') this._throwSnack();
  }

  // ---------- actions ----------
  _nearestMachine(reach) {
    let best = null, bd = reach * reach;
    for (const m of this.machines) {
      if (m.broken) continue;
      const d = dist2(this.player.x, this.player.y, m.x, m.y);
      if (d < bd) { bd = d; best = m; }
    }
    return best;
  }

  _attack(kind) {
    const p = this.player;
    if (this.phase !== 'gameplay' || p.stun > 0 || p.actionCd > 0) return;
    p.actionCd = 0.34;
    p.swing = 1;
    this._addShake(kind === 'kick' ? 4 : 2);
    const m = this._nearestMachine(70);
    if (!m) { this.audio.play(kind); return; }
    // face the machine
    p.angle = Math.atan2(m.y - p.y, m.x - p.x);
    const smash = this._powActive('smash') ? 3 : 1;
    const dmg = (kind === 'kick' ? 2 : 1) * smash + (this._ev('earthquake') ? 1 : 0);
    m.health -= dmg;
    m.hitCount++;
    m.flash = 0.15;
    // physics impulse
    const dir = Math.atan2(m.y - p.y, m.x - p.x);
    const power = (kind === 'kick' ? 70 : 42) * (smash > 1 ? 1.3 : 1);
    m.shakeVx += Math.cos(dir) * power;
    m.shakeVy += Math.sin(dir) * power;
    this.audio.play(kind);
    const t = MACHINE_TYPES[m.type];
    const noise = kind === 'kick' ? t.noise.kick : t.noise.punch;
    p.attackHeat = 3;
    if (m.health <= 0) this._breakMachine(m);
    else {
      this._emitNoise(m.x, m.y, noise);
      this._spark(m.x, m.y, t.color, 6);
    }
  }

  _breakMachine(m) {
    const t = MACHINE_TYPES[m.type];
    m.broken = true;
    m.respawn = 12;
    m.health = 0;
    this._addShake(10);
    this.audio.play('break');
    this._emitNoise(m.x, m.y, t.noise.break);
    this._spark(m.x, m.y, t.color, 22);
    this.player.thefts++;
    this._addCombo();
    this._effectBurst(m.x, m.y);
    const heist = this._ev('heist');
    this._addScore(t.breakBonus * (heist ? 2 : 1), m.x, m.y - 40, t.color, true, ' STEAL');
    // spawn snacks
    const n = Math.floor(rand(t.drop[0], t.drop[1] + 1)) + (heist ? 2 : 0);
    this.player.bestSmash = Math.max(this.player.bestSmash, n);
    for (let i = 0; i < n; i++) {
      const type = t.pool[Math.floor(Math.random() * t.pool.length)];
      const a = rand(0, Math.PI * 2), spd = rand(120, 260);
      this.snacks.push({
        x: m.x + rand(-10, 10), y: m.y + rand(-10, 10),
        vx: Math.cos(a) * spd, vy: Math.sin(a) * spd - 60,
        type, r: SNACKS[type].size * 0.5 + 6, rot: rand(0, 6), vr: rand(-8, 8),
        rest: 0, life: 22,
      });
    }
    this.audio.play('snackfall');
  }

  _interact() {
    const p = this.player;
    if (this.phase !== 'gameplay' || p.stun > 0 || p.buyCd > 0) return;
    const m = this._nearestMachine(80);
    if (!m) return;
    p.buyCd = 0.7;
    // safe purchase: low reward, no noise
    const type = Math.random() < 0.5 ? 'chocolate' : 'soda';
    this.audio.play('buy');
    p.snacks++;
    this._addScore(SNACKS[type].points, m.x, m.y - 30, '#9fe', false, ' buy');
  }

  _dodge(dirX, dirY) {
    const p = this.player;
    if (this.phase !== 'gameplay' || p.stun > 0 || p.dodgeCd > 0) return;
    p.dodgeCd = 1.0; p.dodging = 0.22; p.iframe = 0.4;
    let dx = dirX != null ? dirX : this._moveInput().x;
    let dy = dirY != null ? dirY : this._moveInput().y;
    if (dx === 0 && dy === 0) { dx = Math.cos(p.angle); dy = Math.sin(p.angle); }
    const l = Math.hypot(dx, dy) || 1;
    p.dodgeVx = (dx / l) * 640; p.dodgeVy = (dy / l) * 640;
    this._spark(p.x, p.y, '#8ecbff', 8);
    this.audio.play('shake');
  }

  _powActive(t) { return (this.player.pow[t] || 0) > performance.now() / 1000; }
  _sc(v) { return this._powActive('double') ? v * 2 : v; }

  _comboMult() {
    const c = this.player.combo;
    if (c >= 12) return 4;
    if (c >= 8) return 3;
    if (c >= 5) return 2;
    if (c >= 3) return 1.5;
    return 1;
  }

  _addCombo() {
    const p = this.player;
    p.combo += 1;
    p.comboT = 2.5;
    p.comboBest = Math.max(p.comboBest, p.combo);
    if ([3, 5, 8, 12].includes(p.combo)) {
      this.floaters.push({ x: p.x, y: p.y - 42, text: 'COMBO x' + this._comboMult() + '!', color: '#ffd23f', life: 1.3, vy: -52 });
      this.audio.play('power');
      this._flash('🔥 COMBO x' + this._comboMult());
    }
  }

  _addScore(v, x, y, color, useCombo = true, suffix = '') {
    let mult = 1;
    if (this._powActive('double')) mult *= 2;
    if (this._ev('rush')) mult *= 2;
    if (useCombo) mult *= this._comboMult();
    const gained = Math.round(v * mult);
    this.player.score += gained;
    this.floaters.push({ x, y, text: '+' + gained + suffix, color: color || '#fff', life: 1.1, vy: -40 });
    return gained;
  }

  _emitNoise(x, y, level) {
    const mult = this._powActive('silent') ? 0.12 : 1;
    const lvl = level * mult;
    this.noises.push({ x, y, level: lvl, r: 0, max: lvl * 3.4, age: 0 });
    // alert guards in range (sound ignores LOS)
    for (const g of this.guards) {
      const d = Math.sqrt(dist2(g.x, g.y, x, y));
      if (d < lvl * 3.4) {
        g.alert = Math.min(100, g.alert + lvl * (1 - d / (lvl * 3.4)) * 1.3);
        g.lastX = x; g.lastY = y;
      }
    }
  }

  _spark(x, y, color, n) {
    for (let i = 0; i < n; i++) {
      const a = rand(0, Math.PI * 2), s = rand(40, 260);
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rand(0.3, 0.8), color, r: rand(2, 5) });
    }
  }

  _moveInput() {
    let x = 0, y = 0;
    if (this.keys['KeyW'] || this.keys['ArrowUp']) y -= 1;
    if (this.keys['KeyS'] || this.keys['ArrowDown']) y += 1;
    if (this.keys['KeyA'] || this.keys['ArrowLeft']) x -= 1;
    if (this.keys['KeyD'] || this.keys['ArrowRight']) x += 1;
    if (this.isMobile) { x = this.joy.x; y = this.joy.y; }
    return { x, y };
  }

  // ---------- update ----------
  _loop(now) {
    if (!this.running) return;
    let dt = (now - this._lastT) / 1000;
    this._lastT = now;
    dt = Math.min(dt, 0.05);
    this.update(dt);
    this.render();
    this._raf = requestAnimationFrame(this._loop);
  }

  update(dt) {
    this._syncPhase(dt);
    if (this.phase === 'gameplay') {
      if (this.player.comboT > 0) { this.player.comboT -= dt; if (this.player.comboT <= 0) this.player.combo = 0; }
      this._updateEvents(dt);
      this._updatePlayer(dt);
      this._updateMachines(dt);
      this._updateSnacks(dt);
      this._updateGuards(dt);
      this._updateBoss(dt);
      this._updatePeels(dt);
      this._updateProjectiles(dt);
      this._updateDecoy(dt);
      this._updatePet(dt);
      this._updatePowerups(dt);
    }
    this._updateTrail(dt);
    this._updateNoises(dt);
    if (this.shakeT > 0) this.shakeT -= dt;
    this.resultsAnimT = this.phase === 'results' ? Math.min(1.2, (this.resultsAnimT || 0) + dt * 1.2) : 0;
    this._updateParticles(dt);
    this._updateFloaters(dt);
    if (this.message) { this.messageT -= dt; if (this.messageT <= 0) this.message = null; }

    // networking
    this._netT += dt;
    if (this._netT > 0.12 && this.net) {
      this._netT = 0;
      this.net.send({ x: Math.round(this.player.x), y: Math.round(this.player.y),
        score: this.player.score, suspicion: Math.round(this._suspicion()) });
    }
    // hud
    this._hudT += dt;
    if (this._hudT > 0.1) { this._hudT = 0; this._pushHud(); }
  }

  _updatePlayer(dt) {
    const p = this.player;
    if (p.actionCd > 0) p.actionCd -= dt;
    if (p.buyCd > 0) p.buyCd -= dt;
    if (p.dodgeCd > 0) p.dodgeCd -= dt;
    if (p.gadgetCd > 0) p.gadgetCd -= dt;
    if (p.throwCd > 0) p.throwCd -= dt;
    if (p.swing > 0) p.swing -= dt * 4;
    if (p.attackHeat > 0) p.attackHeat -= dt;
    if (p.hitFlash > 0) p.hitFlash -= dt;
    if (p.stun > 0) { p.stun -= dt; p.vx *= 0.8; p.vy *= 0.8; this._moveResolve(dt); return; }

    const mv = this._moveInput();
    const len = Math.hypot(mv.x, mv.y);
    const moving = len > 0.1;
    // stamina: sprinting drains, walking/idle regenerates. Can't re-sprint until recovered a bit.
    const wantSprint = (this.keys['ShiftLeft'] || this.keys['ShiftRight'] || this.mobileSprint) && moving;
    if (wantSprint && !p.exhausted) { p.stamina = Math.max(0, p.stamina - 30 * dt); if (p.stamina <= 0) p.exhausted = true; }
    else { p.stamina = Math.min(100, p.stamina + (moving ? 20 : 34) * dt); if (p.exhausted && p.stamina >= 25) p.exhausted = false; }
    const sprinting = wantSprint && !p.exhausted && p.stamina > 0;
    let maxSpeed = 260;
    if (sprinting) maxSpeed = 460;
    if (this._powActive('speed')) maxSpeed *= 1.45;
    if (this._ev('sugar')) maxSpeed *= 1.4;
    if (moving) {
      const nx = mv.x / len, ny = mv.y / len;
      // snappy arcade acceleration — quick to reach target, quick to change direction
      const k = Math.min(1, dt * 18);
      p.vx += (nx * maxSpeed - p.vx) * k;
      p.vy += (ny * maxSpeed - p.vy) * k;
      if (!this.isMobile) p.angle = Math.atan2(this.mouse.worldY - p.y, this.mouse.worldX - p.x);
      else p.angle = Math.atan2(ny, nx);
    } else {
      // crisp stop — snappy deceleration
      p.vx *= Math.pow(0.0016, dt); p.vy *= Math.pow(0.0016, dt);
    }
    const sp = Math.hypot(p.vx, p.vy);
    try { window.__snackout = { speed: Math.round(sp), sprinting, x: Math.round(p.x), y: Math.round(p.y) }; } catch (e) {}

    if (p.iframe > 0) p.iframe -= dt;
    if (p.dodging > 0) { p.dodging -= dt; p.vx = p.dodgeVx; p.vy = p.dodgeVy; p.dodgeVx *= 0.86; p.dodgeVy *= 0.86; }

    this._moveResolve(dt);

    // movement noise
    if (sp > 40) {
      const running = sprinting ? 16 : 7;
      if (Math.random() < dt * 2) this._emitNoise(p.x, p.y, running);
    }

    // snack collection (magnet)
    const baseR = 30 + p.r;
    const magnet = this._powActive('magnet') || this._ev('magnet');
    const collectR = magnet ? 190 : baseR;
    for (const s of this.snacks) {
      if (s.collected) continue;
      const d = Math.sqrt(dist2(p.x, p.y, s.x, s.y));
      if (magnet && d < collectR && d > baseR) {
        const a = Math.atan2(p.y - s.y, p.x - s.x);
        s.vx += Math.cos(a) * 900 * dt; s.vy += Math.sin(a) * 900 * dt;
      }
      if (d < baseR + s.r) {
        s.collected = true;
        const info = SNACKS[s.type];
        p.snacks++;
        this._addCombo();
        this._effectBurst(s.x, s.y);
        this._addScore(info.points, s.x, s.y - 20, info.color, true);
        p.recentSnacks.push(s.type); if (p.recentSnacks.length > 6) p.recentSnacks.shift();
        this.audio.play(s.type === 'golden' ? 'golden' : 'collect');
      }
    }
    this.snacks = this.snacks.filter((s) => !s.collected);

    // power-up pickup
    for (const pu of this.powerups) {
      if (!pu.active) continue;
      if (dist2(p.x, p.y, pu.x, pu.y) < (p.r + 26) ** 2) {
        pu.active = false; pu.respawn = 22;
        p.pow[pu.type] = performance.now() / 1000 + POWERUPS[pu.type].dur;
        this.audio.play('power');
        this._flash(POWERUPS[pu.type].name + '!');
        this.floaters.push({ x: pu.x, y: pu.y - 20, text: POWERUPS[pu.type].emoji, color: POWERUPS[pu.type].color, life: 1.2, vy: -50 });
      }
    }
  }

  _moveResolve(dt) {
    const p = this.player;
    p.x += p.vx * dt; p.y += p.vy * dt;
    p.x = clamp(p.x, p.r, WORLD.w - p.r);
    p.y = clamp(p.y, p.r, WORLD.h - p.r);
    // building collisions
    for (const b of this.buildings) {
      const hit = circleRect(p.x, p.y, p.r, b.x, b.y, b.w, b.h);
      if (hit) {
        const d = Math.hypot(hit.dx, hit.dy) || 1;
        const push = p.r - d;
        p.x += (hit.dx / d) * push; p.y += (hit.dy / d) * push;
        p.vx *= 0.5; p.vy *= 0.5;
      }
    }
    // machine collision (solid body)
    for (const m of this.machines) {
      const hit = circleRect(p.x, p.y, p.r, m.x - m.w / 2, m.y - m.h / 2, m.w, m.h);
      if (hit) {
        const d = Math.hypot(hit.dx, hit.dy) || 1;
        const push = p.r - d;
        p.x += (hit.dx / d) * push; p.y += (hit.dy / d) * push;
      }
    }
  }

  _updateMachines(dt) {
    for (const m of this.machines) {
      if (m.flash > 0) m.flash -= dt;
      m.sway += dt * 1.6;
      m.doorOpen = clamp(m.doorOpen + (m.broken ? 1 : -1) * dt * 4, 0, 1);
      // spring physics on shake offset
      m.shakeVx += -m.shakeX * 60 * dt;
      m.shakeVy += -m.shakeY * 60 * dt;
      m.shakeVx *= 0.86; m.shakeVy *= 0.86;
      m.shakeX += m.shakeVx * dt; m.shakeY += m.shakeVy * dt;
      if (m.broken) {
        m.respawn -= dt;
        if (m.respawn <= 0) {
          m.broken = false; m.health = m.maxHealth; m.hitCount = 0;
        }
      }
    }
  }

  _updateSnacks(dt) {
    for (const s of this.snacks) {
      s.life -= dt;
      s.vy += 40 * dt; // slight settling drift
      s.vx *= 0.9; s.vy *= 0.9;
      s.x += s.vx * dt; s.y += s.vy * dt;
      s.rot += s.vr * dt; s.vr *= 0.96;
      // world bounds bounce
      if (s.x < s.r) { s.x = s.r; s.vx *= -0.5; }
      if (s.x > WORLD.w - s.r) { s.x = WORLD.w - s.r; s.vx *= -0.5; }
      if (s.y < s.r) { s.y = s.r; s.vy *= -0.5; }
      if (s.y > WORLD.h - s.r) { s.y = WORLD.h - s.r; s.vy *= -0.5; }
      // bounce off buildings
      for (const b of this.buildings) {
        const hit = circleRect(s.x, s.y, s.r, b.x, b.y, b.w, b.h);
        if (hit) {
          const d = Math.hypot(hit.dx, hit.dy) || 1;
          s.x += (hit.dx / d) * (s.r - d); s.y += (hit.dy / d) * (s.r - d);
          s.vx *= -0.45; s.vy *= -0.45; s.vr = rand(-6, 6);
        }
      }
    }
    this.snacks = this.snacks.filter((s) => s.life > 0);
  }

  _updateGuards(dt) {
    const p = this.player;
    for (const g of this.guards) {
      if (g.stunT > 0) { g.stunT -= dt; continue; }
      const seePlayer = this._guardSees(g, p);
      if (seePlayer && !this._powActive('invisible')) {
        g.lastX = p.x; g.lastY = p.y; g.seenT = 2.5;
        const d = Math.sqrt(dist2(g.x, g.y, p.x, p.y));
        const near = 1 - d / g.range;
        const suspiciousAct = p.attackHeat > 0 || Math.hypot(p.vx, p.vy) > 320;
        g.alert = Math.min(100, g.alert + (suspiciousAct ? 72 : 20) * near * dt);
      } else {
        g.seenT -= dt;
        g.alert -= (g.chasing ? 6 : 11) * dt;
      }
      g.alert = clamp(g.alert, 0, 100);

      const wasChasing = g.chasing;
      if (g.alert >= 90) g.state = 'chase';
      else if (g.alert >= 55) g.state = 'alert';
      else if (g.alert >= 25) g.state = 'investigate';
      else g.state = 'patrol';
      g.chasing = g.state === 'chase';

      if (g.state === 'patrol') {
        this._guardPatrol(g, dt);
      } else if (g.state === 'investigate' || g.state === 'alert') {
        this._guardGoto(g, g.lastX, g.lastY, dt, 150);
        if (dist2(g.x, g.y, g.lastX, g.lastY) < 40 * 40) {
          g.angle += dt * 2; // look around
        }
      } else if (g.state === 'chase') {
        if (!wasChasing) { this.audio.play('chase'); this._flash('⚠ GUARD SPOTTED YOU!'); }
        const tx = g.seenT > 0 ? p.x : g.lastX;
        const ty = g.seenT > 0 ? p.y : g.lastY;
        this._guardGoto(g, tx, ty, dt, 220);
        // catch?
        if (!this._powActive('shield') && p.stun <= 0 && p.iframe <= 0 && dist2(g.x, g.y, p.x, p.y) < (g.r + p.r + 6) ** 2) {
          this._caught(g);
        }
      }

      if (wasChasing && !g.chasing && this.phase === 'gameplay') {
        // escaped
        this._addScore(30, p.x, p.y - 30, '#3ddc84', false, ' ESCAPE');
        this.audio.play('escape'); this._flash('ESCAPED! +30');
      }
    }
  }

  _guardSees(g, p) {
    if (this._ev('guardbreak')) return false;
    let range = g.range;
    if (this._ev('blackout')) range *= 0.45;
    const d = Math.sqrt(dist2(g.x, g.y, p.x, p.y));
    if (d > range) return false;
    const a = Math.atan2(p.y - g.y, p.x - g.x);
    let diff = Math.abs(a - g.angle);
    while (diff > Math.PI) diff = Math.abs(diff - Math.PI * 2);
    if (diff > g.fov / 2) return false;
    for (const b of this.buildings) if (segRect(g.x, g.y, p.x, p.y, b)) return false;
    return true;
  }

  _guardPatrol(g, dt) {
    const [tx, ty] = g.path[g.wp];
    if (dist2(g.x, g.y, tx, ty) < 30 * 30) g.wp = (g.wp + 1) % g.path.length;
    this._guardGoto(g, tx, ty, dt, g.speed);
  }

  _guardGoto(g, tx, ty, dt, speed) {
    const dx = tx - g.x, dy = ty - g.y;
    const d = Math.hypot(dx, dy) || 1;
    const targetAngle = Math.atan2(dy, dx);
    // smooth turn
    let da = targetAngle - g.angle;
    while (da > Math.PI) da -= Math.PI * 2;
    while (da < -Math.PI) da += Math.PI * 2;
    g.angle += da * Math.min(1, dt * 8);
    if (d > 6) {
      let nx = g.x + (dx / d) * speed * dt;
      let ny = g.y + (dy / d) * speed * dt;
      // avoid walking through buildings
      let blocked = false;
      for (const b of this.buildings) if (circleRect(nx, ny, g.r, b.x, b.y, b.w, b.h)) blocked = true;
      if (blocked) { nx = g.x + Math.cos(g.angle + 1.2) * speed * dt; ny = g.y + Math.sin(g.angle + 1.2) * speed * dt; }
      g.x = clamp(nx, g.r, WORLD.w - g.r); g.y = clamp(ny, g.r, WORLD.h - g.r);
    }
  }

  _caught(g) {
    const p = this.player;
    const loss = Math.round(p.score * 0.25);
    p.score = Math.max(0, p.score - loss);
    p.caught++;
    p.stun = 1.5; p.hitFlash = 1.2; p.combo = 0; p.comboT = 0;
    this._addShake(12);
    this.audio.play('caught');
    this._flash('CAUGHT! -' + loss + ' pts');
    this.floaters.push({ x: p.x, y: p.y - 30, text: '-' + loss, color: '#ff5a5f', life: 1.4, vy: -46 });
    // drop recent snacks
    for (let i = 0; i < Math.min(3, p.recentSnacks.length); i++) {
      const type = p.recentSnacks.pop();
      const a = rand(0, Math.PI * 2);
      this.snacks.push({ x: p.x, y: p.y, vx: Math.cos(a) * 200, vy: Math.sin(a) * 200,
        type, r: SNACKS[type].size * 0.5 + 6, rot: 0, vr: rand(-6, 6), rest: 0, life: 18 });
    }
    // respawn at farthest spawn
    let best = this.spawns[0], bd = -1;
    for (const s of this.spawns) {
      const d = dist2(s[0], s[1], g.x, g.y);
      if (d > bd) { bd = d; best = s; }
    }
    p.x = best[0]; p.y = best[1]; p.vx = 0; p.vy = 0;
    // calm guards
    for (const gg of this.guards) { gg.alert = 0; gg.chasing = false; gg.state = 'patrol'; }
  }

  _updatePowerups(dt) {
    for (const pu of this.powerups) {
      pu.bob += dt * 3;
      if (!pu.active) { pu.respawn -= dt; if (pu.respawn <= 0) pu.active = true; }
    }
  }

  _updateBoss(dt) {
    const p = this.player;
    if (!this.bossSpawned) {
      const lb = this._leaderboard();
      const leaderMe = lb[0] && lb[0].me;
      const elapsed = MATCH.duration - this.timeLeft;
      if ((leaderMe && elapsed > 30) || elapsed > 100) this._spawnBoss();
      return;
    }
    const b = this.boss;
    if (!b) return;
    if (b.stunT > 0) { b.stunT -= dt; return; }
    b.pulse += dt * 4;
    if (this._ev('guardbreak')) { this._guardGoto(b, b.lastX, b.lastY, dt, 60); return; }
    const lb = this._leaderboard();
    const targetMe = lb[0] && lb[0].me && !this._powActive('invisible');
    let tx, ty, speed;
    if (targetMe) { tx = p.x; ty = p.y; b.lastX = p.x; b.lastY = p.y; speed = 215; }
    else {
      tx = b.lastX; ty = b.lastY; speed = 150;
      if (dist2(b.x, b.y, tx, ty) < 60 * 60) { b.lastX = rand(200, WORLD.w - 200); b.lastY = rand(200, WORLD.h - 200); }
    }
    this._guardGoto(b, tx, ty, dt, speed);
    if (targetMe && !this._powActive('shield') && p.stun <= 0 && p.iframe <= 0 && dist2(b.x, b.y, p.x, p.y) < (b.r + p.r + 6) ** 2) {
      this._caughtByBoss(b);
    }
  }

  _spawnBoss() {
    this.bossSpawned = true;
    const s = this.spawns[Math.floor(Math.random() * this.spawns.length)];
    this.boss = { x: s[0], y: s[1], r: 28, angle: 0, range: 420, fov: Math.PI * 0.7, pulse: 0, lastX: s[0], lastY: s[1], stunT: 0 };
    this._addShake(10);
    this.audio.play('chase');
    this._flash('🕴️ THE MANAGER IS HUNTING THE LEADER!');
  }

  _caughtByBoss(b) {
    const p = this.player;
    const loss = Math.round(p.score * 0.35);
    p.score = Math.max(0, p.score - loss);
    p.caught++; p.stun = 1.6; p.hitFlash = 1.2; p.combo = 0; p.comboT = 0;
    this._addShake(14);
    this.audio.play('caught');
    this._flash('MANAGER CAUGHT YOU! -' + loss);
    this.floaters.push({ x: p.x, y: p.y - 30, text: '-' + loss, color: '#ff2d55', life: 1.4, vy: -46 });
    let best = this.spawns[0], bd = -1;
    for (const s of this.spawns) { const d = dist2(s[0], s[1], b.x, b.y); if (d > bd) { bd = d; best = s; } }
    p.x = best[0]; p.y = best[1]; p.vx = 0; p.vy = 0;
    b.lastX = rand(200, WORLD.w - 200); b.lastY = rand(200, WORLD.h - 200);
  }

  _effectBurst(x, y) {
    const e = this.profile.effect || 'none';
    if (e === 'none') return;
    const map = { sparkle: ['#ffffff', '#ffd23f'], stars: ['#ffd23f', '#fff5c0'], hearts: ['#ff6fb5', '#ff9ecb'], fire: ['#ff5a3d', '#ffd23f'], rainbow: ['#ff5a5f', '#ffd23f', '#3ddc84', '#4d8bff', '#b06bff'] };
    const cols = map[e] || ['#fff'];
    for (let i = 0; i < 9; i++) { const a = rand(0, 6.28), s = rand(60, 220); this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 40, life: rand(0.4, 0.9), color: cols[i % cols.length], r: rand(2, 5) }); }
  }

  _ev(t) { return this.event && this.event.type === t; }

  _updateEvents(dt) {
    if (this.event) {
      this.event.t -= dt;
      if (this.event.type === 'snackrain') {
        this.event.rainT = (this.event.rainT || 0) - dt;
        if (this.event.rainT <= 0) { this.event.rainT = 0.45; this._rainSnack(); }
      }
      if (this.event.type === 'earthquake') {
        for (const m of this.machines) if (!m.broken) { m.shakeVx += rand(-40, 40); m.shakeVy += rand(-40, 40); }
      }
      if (this.event.type === 'heist') {
        for (const g of this.guards) if (g.stunT <= 0) g.alert = Math.min(100, g.alert + 9 * dt);
      }
      if (this.event.t <= 0) { this.event = null; this.eventCd = rand(8, 13); }
    } else {
      this.eventCd -= dt;
      if (this.eventCd <= 0) this._startEvent();
    }
  }

  _startEvent() {
    const keys = Object.keys(EVENTS);
    const type = keys[Math.floor(Math.random() * keys.length)];
    this.event = { type, t: EVENTS[type].dur };
    this._addShake(6);
    this.audio.play('power');
    this._flash(EVENTS[type].emoji + ' ' + EVENTS[type].name + '!');
  }

  _rainSnack() {
    const c = this._cam || { x: 0, y: 0 };
    const x = rand(c.x + 60, c.x + this.canvas.width - 60);
    const y = c.y - 20;
    const pool = ['chocolate', 'soda', 'popcorn', 'cookie', 'candy', 'donut'];
    const type = pool[Math.floor(Math.random() * pool.length)];
    this.snacks.push({ x, y, vx: rand(-30, 30), vy: rand(80, 140), type, r: SNACKS[type].size * 0.5 + 6, rot: rand(0, 6), vr: rand(-8, 8), rest: 0, life: 18 });
  }

  _nearestGuard(x, y, range) {
    let best = null, bd = range * range;
    const all = this.boss ? [...this.guards, this.boss] : this.guards;
    for (const g of all) { const d = dist2(x, y, g.x, g.y); if (d < bd) { bd = d; best = g; } }
    return best;
  }

  _useGadget() {
    const p = this.player;
    if (this.phase !== 'gameplay' || p.stun > 0 || p.gadgetCd > 0) return;
    const id = this.profile.gadget || 'none';
    if (id === 'none') return;
    p.gadgetCd = GADGETS[id].cd;
    const now = performance.now() / 1000;
    if (id === 'banana') { this.peels.push({ x: p.x, y: p.y, life: 22 }); this.audio.play('shake'); this._flash('🍌 Trap set!'); }
    else if (id === 'jetpack') { p.dodging = 0.4; p.dodgeVx = Math.cos(p.angle) * 840; p.dodgeVy = Math.sin(p.angle) * 840; p.pow.speed = now + 3; this.audio.play('go'); this._spark(p.x, p.y, '#ffd23f', 16); this._flash('🚀 Jetpack!'); }
    else if (id === 'smoke') { for (const g of this.guards) { g.alert = 0; g.chasing = false; g.state = 'patrol'; } p.pow.invisible = now + 4; this._spark(p.x, p.y, '#cfd6e6', 30); this.audio.play('power'); this._flash('💨 Smoke Bomb!'); }
    else if (id === 'snackbomb') { const m = this._nearestMachine(240); if (m) { p.angle = Math.atan2(m.y - p.y, m.x - p.x); this._breakMachine(m); this._flash('🧨 BOOM!'); } else { p.gadgetCd = 1; this._flash('No machine nearby'); } }
    else if (id === 'decoy') { this.decoy = { x: p.x + Math.cos(p.angle) * 70, y: p.y + Math.sin(p.angle) * 70, life: 6, t: 0, pulse: 0 }; this.audio.play('power'); this._flash('🪀 Decoy out!'); }
    else if (id === 'disguise') { p.pow.invisible = now + 6; this.audio.play('power'); this._flash('🥸 Disguised!'); }
  }

  _throwSnack() {
    const p = this.player;
    if (this.phase !== 'gameplay' || p.stun > 0 || p.throwCd > 0) return;
    p.throwCd = 0.75;
    const a = this.isMobile ? p.angle : Math.atan2(this.mouse.worldY - p.y, this.mouse.worldX - p.x);
    p.angle = a;
    const type = ['soda', 'donut', 'candy'][Math.floor(Math.random() * 3)];
    this.projectiles.push({ x: p.x, y: p.y, vx: Math.cos(a) * 640, vy: Math.sin(a) * 640, life: 1.2, type });
    this.audio.play('snackfall');
  }

  _updatePeels(dt) {
    const all = this.boss ? [...this.guards, this.boss] : this.guards;
    for (const peel of this.peels) {
      peel.life -= dt;
      for (const g of all) {
        if (g.stunT > 0) continue;
        if (dist2(peel.x, peel.y, g.x, g.y) < (g.r + 12) ** 2) {
          g.stunT = 2.4; g.alert = 0; g.chasing = false; g.state = 'patrol';
          this._spark(peel.x, peel.y, '#ffd23f', 12); this.audio.play('shake');
          this.floaters.push({ x: g.x, y: g.y - 24, text: 'SLIP!', color: '#ffd23f', life: 1, vy: -40 });
          peel.life = 0;
        }
      }
    }
    this.peels = this.peels.filter((p) => p.life > 0);
  }

  _updateProjectiles(dt) {
    const all = this.boss ? [...this.guards, this.boss] : this.guards;
    for (const pr of this.projectiles) {
      pr.life -= dt; pr.x += pr.vx * dt; pr.y += pr.vy * dt;
      for (const g of all) {
        if (dist2(pr.x, pr.y, g.x, g.y) < (g.r + 10) ** 2) {
          g.stunT = 2; g.alert = 0; g.chasing = false; g.state = 'patrol';
          this._spark(pr.x, pr.y, SNACKS[pr.type].color, 10); this.audio.play('punch');
          this.floaters.push({ x: g.x, y: g.y - 24, text: 'STUNNED!', color: '#ffd23f', life: 1, vy: -40 });
          pr.life = 0;
        }
      }
      for (const b of this.buildings) if (circleRect(pr.x, pr.y, 6, b.x, b.y, b.w, b.h)) pr.life = 0;
    }
    this.projectiles = this.projectiles.filter((p) => p.life > 0);
  }

  _updateDecoy(dt) {
    if (!this.decoy) return;
    const d = this.decoy;
    d.life -= dt; d.t -= dt; d.pulse += dt * 5;
    if (d.t <= 0) { d.t = 0.4; this._emitNoise(d.x, d.y, 70); }
    if (d.life <= 0) this.decoy = null;
  }

  _updatePet(dt) {
    if (!this.pet) return;
    const pet = this.pet, p = this.player;
    const tx = p.x - Math.cos(p.angle) * 38, ty = p.y - Math.sin(p.angle) * 38;
    pet.x += (tx - pet.x) * Math.min(1, dt * 6);
    pet.y += (ty - pet.y) * Math.min(1, dt * 6);
    pet.bob += dt * 6;
    const rr = pet.id === 'bot' ? 160 : (pet.id === 'dog' ? 60 : 95);
    for (const s of this.snacks) {
      if (s.collected) continue;
      if (dist2(pet.x, pet.y, s.x, s.y) < rr * rr) {
        s.collected = true;
        const info = SNACKS[s.type];
        p.snacks++;
        this._addScore(info.points, s.x, s.y - 20, info.color, false);
        this.audio.play(s.type === 'golden' ? 'golden' : 'collect');
      }
    }
    this.snacks = this.snacks.filter((s) => !s.collected);
    pet.t -= dt;
    if (pet.t <= 0) {
      pet.t = 3;
      if (pet.id === 'dog') { const g = this._nearestGuard(pet.x, pet.y, 260); if (g && g.stunT <= 0) { g.alert = Math.max(0, g.alert - 55); this.floaters.push({ x: pet.x, y: pet.y - 20, text: 'WOOF!', color: '#c68a4b', life: 0.9, vy: -36 }); } }
      if (pet.id === 'dragon') { const g = this._nearestGuard(pet.x, pet.y, 300); if (g && g.stunT <= 0) { g.stunT = 2; this._spark(g.x, g.y, '#b06bff', 12); this.floaters.push({ x: g.x, y: g.y - 22, text: '🔥', color: '#ff5a3d', life: 0.9, vy: -36 }); } }
    }
  }

  _updateTrail(dt) {
    for (const t of this.trailPts) t.life -= dt;
    this.trailPts = this.trailPts.filter((t) => t.life > 0);
    const skinCols = SKIN_TRAILS[this.profile.skin];
    const cols = (skinCols && skinCols.length) ? skinCols : (TRAILS[this.profile.trail]?.colors || []);
    if (cols.length && this.phase === 'gameplay' && Math.hypot(this.player.vx, this.player.vy) > 70) {
      if (!this._trailT || performance.now() - this._trailT > 45) {
        this._trailT = performance.now();
        this.trailPts.push({ x: this.player.x, y: this.player.y, life: 0.5, color: cols[Math.floor(Math.random() * cols.length)] });
        if (this.trailPts.length > 40) this.trailPts.shift();
      }
    }
  }

  _updateNoises(dt) {
    for (const n of this.noises) { n.age += dt; n.r = Math.min(n.max, n.r + n.max * dt * 3); }
    this.noises = this.noises.filter((n) => n.age < 0.6);
  }
  _updateParticles(dt) {
    for (const pa of this.particles) { pa.life -= dt; pa.x += pa.vx * dt; pa.y += pa.vy * dt; pa.vx *= 0.9; pa.vy *= 0.9; }
    this.particles = this.particles.filter((p) => p.life > 0);
  }
  _updateFloaters(dt) {
    for (const f of this.floaters) { f.life -= dt; f.y += f.vy * dt; f.vy *= 0.94; }
    this.floaters = this.floaters.filter((f) => f.life > 0);
  }

  _flash(msg) { this.message = msg; this.messageT = 1.6; }

  _addShake(mag) { this.shakeMag = Math.max(this.shakeMag, mag); this.shakeT = 0.35; }

  _lighten(hex, amt) {
    let c = String(hex).replace('#', '');
    if (c.length === 3) c = c.split('').map((x) => x + x).join('');
    let r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
    if (isNaN(r)) return hex;
    r = clamp(r + amt, 0, 255); g = clamp(g + amt, 0, 255); b = clamp(b + amt, 0, 255);
    return `rgb(${r | 0},${g | 0},${b | 0})`;
  }

  _suspicion() {
    let m = 0;
    for (const g of this.guards) {
      if (g.chasing) return 100;
      m = Math.max(m, g.alert);
    }
    return m;
  }

  _leaderboard() {
    const list = [{ id: this.net ? this.net.selfId : 'me', name: this.profile.name, score: this.player.score, me: true }];
    for (const r of this.remote) list.push({ id: r.id, name: r.name, score: r.score, me: false });
    list.sort((a, b) => b.score - a.score);
    return list;
  }

  _pushHud() {
    const s = this._suspicion();
    let label = 'NORMAL', color = '#3ddc84';
    if (s >= 90) { label = 'CHASE'; color = '#ff2d55'; }
    else if (s >= 55) { label = 'ALERT'; color = '#ff8a3d'; }
    else if (s >= 25) { label = 'SUSPICIOUS'; color = '#ffd23f'; }
    const now = performance.now() / 1000;
    const pows = Object.keys(this.player.pow)
      .filter((t) => this.player.pow[t] > now)
      .map((t) => ({ type: t, emoji: POWERUPS[t].emoji, name: POWERUPS[t].name, left: Math.ceil(this.player.pow[t] - now) }));
    const lb = this._leaderboard();
    const rank = lb.findIndex((x) => x.me) + 1;
    this.onHud({
      phase: this.phase,
      countdown: this.phase === 'countdown' ? Math.ceil(this.phaseT) : 0,
      timeLeft: Math.max(0, Math.ceil(this.timeLeft)),
      score: this.player.score,
      snacks: this.player.snacks,
      thefts: this.player.thefts,
      rank, total: lb.length,
      suspicion: Math.round(s), suspLabel: label, suspColor: color,
      powerups: pows,
      leaderboard: lb.slice(0, 8),
      message: this.message,
      players: this.remoteCount + 1,
      combo: this.player.combo,
      comboMult: this._comboMult(),
      comboT: Math.max(0, this.player.comboT),
      boss: !!this.boss,
      results: this.phase === 'results' ? this.results : null,
      nextIn: this.phase === 'results' ? Math.max(0, Math.ceil(this.resultsT || 0)) : 0,
      coins: Math.round(this.player.score / 10) + this.player.thefts * 2,
      event: this.event ? { name: EVENTS[this.event.type].name, emoji: EVENTS[this.event.type].emoji, color: EVENTS[this.event.type].color, tint: EVENTS[this.event.type].tint, timeLeft: Math.ceil(this.event.t) } : null,
      gadget: (this.profile.gadget && this.profile.gadget !== 'none') ? { icon: GADGETS[this.profile.gadget].icon, name: GADGETS[this.profile.gadget].name, cd: Math.max(0, this.player.gadgetCd), maxCd: GADGETS[this.profile.gadget].cd } : null,
      throwCd: Math.max(0, this.player.throwCd),
      stamina: Math.round(this.player.stamina),
      exhausted: this.player.exhausted,
      dodgeCd: Math.max(0, this.player.dodgeCd),
    });
  }

  _endMatch() {
    if (this.results) return;
    this.audio.play('end');
    const lb = this._leaderboard();
    const rank = lb.findIndex((x) => x.me) + 1;
    const p = this.player;
    const coins = Math.round(p.score / 10) + p.thefts * 2;
    const results = {
      score: p.score, rank: rank || lb.length, total: lb.length, snacks: p.snacks, thefts: p.thefts,
      caught: p.caught, bestSmash: p.bestSmash, combo: p.comboBest, coins,
      leaderboard: lb.slice(0, 10),
    };
    this.results = results;
    if (!this._awarded) { this._awarded = true; this.onMatchEnd(results); }
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      fetch(base + '/api/score', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: this.profile.name, score: p.score, snacks: p.snacks, thefts: p.thefts, caught: p.caught }),
      }).catch(() => {});
    } catch (e) {}
    this.onResults(results);
  }

  // ---------- render ----------
  render() {
    const ctx = this.ctx;
    const W = this.canvas.width, H = this.canvas.height;
    // camera
    const camX = clamp(this.player.x - W / 2, 0, WORLD.w - W);
    const camY = clamp(this.player.y - H / 2, 0, WORLD.h - H);
    this._cam = { x: WORLD.w <= W ? (WORLD.w - W) / 2 : camX, y: WORLD.h <= H ? (WORLD.h - H) / 2 : camY };
    this.mouse.worldX = this.mouse.x + this._cam.x;
    this.mouse.worldY = this.mouse.y + this._cam.y;

    ctx.clearRect(0, 0, W, H);
    ctx.save();
    const sh = this.shakeT > 0 ? this.shakeMag * (this.shakeT / 0.35) : 0;
    ctx.translate(-this._cam.x + (Math.random() * 2 - 1) * sh, -this._cam.y + (Math.random() * 2 - 1) * sh);
    if (this.phase === 'results') {
      const z = 1 + Math.min(1, this.resultsAnimT || 0) * 0.5;
      ctx.translate(this.player.x, this.player.y); ctx.scale(z, z); ctx.translate(-this.player.x, -this.player.y);
    }

    this._drawGround(ctx);
    this._drawNoises(ctx);
    this._drawPeels(ctx);
    this._drawDecoy(ctx);
    this._drawPowerups(ctx);
    this._drawDeco(ctx);
    this._drawBuildings(ctx);
    this._drawMachines(ctx);
    this._drawSnacks(ctx);
    this._drawGuards(ctx);
    this._drawBoss(ctx);
    this._drawRemotePlayers(ctx);
    this._drawTrail(ctx);
    this._drawParticles(ctx);
    this._drawPlayer(ctx);
    this._drawPet(ctx);
    this._drawProjectiles(ctx);
    this._drawFloaters(ctx);

    ctx.restore();
    // lighting vignette for depth
    const vg = ctx.createRadialGradient(W / 2, H * 0.42, Math.min(W, H) * 0.28, W / 2, H / 2, Math.max(W, H) * 0.72);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.38)');
    ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);
    this._drawMinimap(ctx, W, H);
  }

  _drawGround(ctx) {
    const c = this._cam;
    // asphalt base
    ctx.fillStyle = '#2a2f45';
    ctx.fillRect(c.x, c.y, this.canvas.width, this.canvas.height);
    // tile grid
    const t = 130;
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 2;
    const sx = Math.floor(c.x / t) * t, sy = Math.floor(c.y / t) * t;
    ctx.beginPath();
    for (let x = sx; x < c.x + this.canvas.width + t; x += t) { ctx.moveTo(x, c.y); ctx.lineTo(x, c.y + this.canvas.height); }
    for (let y = sy; y < c.y + this.canvas.height + t; y += t) { ctx.moveTo(c.x, y); ctx.lineTo(c.x + this.canvas.width, y); }
    ctx.stroke();
    // world border
    ctx.strokeStyle = 'rgba(255,210,63,0.25)'; ctx.lineWidth = 8;
    ctx.strokeRect(0, 0, WORLD.w, WORLD.h);
  }

  _rr(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  _drawBuildings(ctx) {
    for (const b of this.buildings) {
      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      this._rr(ctx, b.x + 6, b.y + 10, b.w, b.h, 16); ctx.fill();
      const grad = ctx.createLinearGradient(b.x, b.y, b.x, b.y + b.h);
      const cols = { shop: ['#3d4468', '#2c3252'], plaza: ['#4a5178', '#343a5e'], block: ['#343a58', '#262b45'] };
      const cc = cols[b.kind] || cols.block;
      grad.addColorStop(0, cc[0]); grad.addColorStop(1, cc[1]);
      ctx.fillStyle = grad;
      this._rr(ctx, b.x, b.y, b.w, b.h, 16); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 2; ctx.stroke();
      // roof accent
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      this._rr(ctx, b.x + 10, b.y + 10, b.w - 20, 26, 8); ctx.fill();
      if (b.label) {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.font = '700 22px Fredoka, sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(b.label, b.x + b.w / 2, b.y + b.h / 2);
      }
    }
  }

  _drawDeco(ctx) {
    for (const d of this.deco) {
      ctx.save(); ctx.translate(d.x, d.y);
      if (d.kind === 'bench') { ctx.fillStyle = '#7a5a3a'; this._rr(ctx, -22, -6, 44, 12, 3); ctx.fill(); }
      else if (d.kind === 'trash') { ctx.fillStyle = '#4a5a4a'; this._rr(ctx, -9, -12, 18, 24, 4); ctx.fill(); ctx.fillStyle = '#3a463a'; this._rr(ctx, -11, -14, 22, 5, 2); ctx.fill(); }
      else if (d.kind === 'sign') { ctx.fillStyle = '#8a8f9e'; ctx.fillRect(-2, -2, 4, 26); ctx.fillStyle = '#ffd23f'; this._rr(ctx, -14, -20, 28, 18, 4); ctx.fill(); }
      else if (d.kind === 'plant') { ctx.fillStyle = '#2f8f4f'; ctx.beginPath(); ctx.arc(0, -6, 12, 0, 7); ctx.fill(); ctx.fillStyle = '#5a4030'; this._rr(ctx, -7, 4, 14, 10, 2); ctx.fill(); }
      else if (d.kind === 'cone') { ctx.fillStyle = '#ff7a3d'; ctx.beginPath(); ctx.moveTo(0, -14); ctx.lineTo(10, 8); ctx.lineTo(-10, 8); ctx.closePath(); ctx.fill(); }
      ctx.restore();
    }
  }

  _drawMachines(ctx) {
    for (const m of this.machines) {
      const x = m.x + m.shakeX, y = m.y + m.shakeY;
      const t = MACHINE_TYPES[m.type];
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.sin(m.sway) * 0.025);
      // shadow
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.beginPath(); ctx.ellipse(0, m.h / 2 - 4, m.w / 2, 12, 0, 0, 7); ctx.fill();
      if (m.broken) {
        ctx.globalAlpha = 0.5;
      }
      // 3D depth lip
      ctx.fillStyle = m.broken ? '#2a2a2a' : this._lighten(t.accent, -40);
      this._rr(ctx, -m.w / 2, -m.h / 2 + 10, m.w, m.h, 10); ctx.fill();
      // body
      const grad = ctx.createLinearGradient(-m.w / 2, 0, m.w / 2, 0);
      grad.addColorStop(0, t.accent); grad.addColorStop(0.5, t.color); grad.addColorStop(1, t.accent);
      ctx.fillStyle = m.flash > 0 ? '#ffffff' : grad;
      this._rr(ctx, -m.w / 2, -m.h / 2, m.w, m.h, 10); ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = 3; ctx.stroke();
      // top face highlight (3D)
      ctx.fillStyle = 'rgba(255,255,255,0.16)';
      this._rr(ctx, -m.w / 2, -m.h / 2, m.w, 12, 8); ctx.fill();
      // glass window
      ctx.fillStyle = 'rgba(10,20,40,0.55)';
      this._rr(ctx, -m.w / 2 + 8, -m.h / 2 + 10, m.w - 30, m.h - 34, 6); ctx.fill();
      // snack rows in glass
      if (!m.broken) {
        const foods = ['🍫', '🥤', '🍿', '🍬'];
        ctx.font = '13px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        for (let r = 0; r < 3; r++) for (let cX = 0; cX < 2; cX++) {
          ctx.fillText(foods[(r + cX) % foods.length], -m.w / 2 + 18 + cX * 16, -m.h / 2 + 24 + r * 18);
        }
      }
      // dispenser slot
      ctx.fillStyle = '#111'; this._rr(ctx, -m.w / 2 + 10, m.h / 2 - 20, m.w - 20, 12, 3); ctx.fill();
      // door pops open when smashed
      if (m.doorOpen > 0.02) {
        ctx.save(); ctx.translate(-m.w / 2 + 10, m.h / 2 - 8); ctx.rotate(m.doorOpen * 1.4);
        ctx.fillStyle = 'rgba(12,22,44,0.85)'; this._rr(ctx, 0, 0, m.w - 20, 13, 3); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.restore();
      }
      // damage cracks
      const dmg = 1 - m.health / m.maxHealth;
      if (dmg > 0 && !m.broken) {
        ctx.strokeStyle = 'rgba(0,0,0,0.6)'; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i < Math.ceil(dmg * 5); i++) {
          const ox = rand(-m.w / 3, m.w / 3);
          ctx.moveTo(ox, -m.h / 3); ctx.lineTo(ox + rand(-8, 8), m.h / 4);
        }
        ctx.stroke();
      }
      ctx.restore();

      // health bar when damaged
      if (!m.broken && m.health < m.maxHealth) {
        const bw = 60;
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; this._rr(ctx, x - bw / 2, y - m.h / 2 - 16, bw, 7, 3); ctx.fill();
        ctx.fillStyle = m.health / m.maxHealth > 0.4 ? '#3ddc84' : '#ff5a5f';
        this._rr(ctx, x - bw / 2, y - m.h / 2 - 16, bw * (m.health / m.maxHealth), 7, 3); ctx.fill();
      }
      if (m.broken) {
        ctx.fillStyle = '#ffd23f'; ctx.font = '600 12px Fredoka, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('restocking ' + Math.ceil(m.respawn) + 's', x, y - m.h / 2 - 8);
      } else {
        // label chip
        ctx.fillStyle = t.color; ctx.font = '700 11px Fredoka, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(t.label, x, y + m.h / 2 + 12);
      }
    }
  }

  _drawSnacks(ctx) {
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const s of this.snacks) {
      const info = SNACKS[s.type];
      ctx.save(); ctx.translate(s.x, s.y);
      ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.beginPath(); ctx.ellipse(0, s.r * 0.7, s.r, s.r * 0.4, 0, 0, 7); ctx.fill();
      ctx.rotate(s.rot);
      if (s.type === 'golden') { ctx.shadowColor = '#ffd23f'; ctx.shadowBlur = 16; }
      ctx.font = info.size + 'px serif';
      ctx.fillText(info.emoji, 0, 0);
      ctx.restore();
    }
  }

  _drawGuards(ctx) {
    for (const g of this.guards) {
      // vision cone
      const coneColor = g.state === 'chase' ? 'rgba(255,45,85,0.18)' :
        g.state === 'alert' ? 'rgba(255,138,61,0.16)' :
        g.state === 'investigate' ? 'rgba(255,210,63,0.14)' : 'rgba(120,200,255,0.10)';
      ctx.fillStyle = coneColor;
      ctx.beginPath(); ctx.moveTo(g.x, g.y);
      ctx.arc(g.x, g.y, g.range, g.angle - g.fov / 2, g.angle + g.fov / 2);
      ctx.closePath(); ctx.fill();
      // body shadow
      ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.beginPath(); ctx.ellipse(g.x, g.y + g.r - 2, g.r, 7, 0, 0, 7); ctx.fill();
      // body
      ctx.save(); ctx.translate(g.x, g.y);
      ctx.fillStyle = '#1f2740'; ctx.beginPath(); ctx.arc(0, 0, g.r, 0, 7); ctx.fill();
      ctx.fillStyle = '#2c3a63'; ctx.beginPath(); ctx.arc(0, 0, g.r - 5, 0, 7); ctx.fill();
      // facing cap
      ctx.rotate(g.angle);
      ctx.fillStyle = '#0f1526'; this._rr(ctx, 2, -8, 14, 16, 4); ctx.fill();
      ctx.fillStyle = '#ffd23f'; ctx.fillRect(g.r - 4, -3, 5, 6);
      ctx.restore();
      // badge / state icon
      const icon = g.state === 'chase' ? '!' : g.state === 'alert' ? '?' : g.state === 'investigate' ? '·' : '';
      if (icon) { ctx.fillStyle = '#fff'; ctx.font = '700 20px Fredoka, sans-serif'; ctx.textAlign = 'center'; ctx.fillText(icon, g.x, g.y - g.r - 12); }
      ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.font = '600 11px Fredoka, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('GUARD', g.x, g.y + g.r + 12);
    }
  }

  _drawBoss(ctx) {
    const b = this.boss;
    if (!b) return;
    ctx.fillStyle = 'rgba(255,45,85,0.12)';
    ctx.beginPath(); ctx.arc(b.x, b.y, b.range, 0, 7); ctx.fill();
    ctx.save(); ctx.translate(b.x, b.y);
    ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.beginPath(); ctx.ellipse(0, b.r - 2, b.r, 9, 0, 0, 7); ctx.fill();
    const glow = 22 + Math.sin(b.pulse) * 6;
    ctx.shadowColor = '#ff2d55'; ctx.shadowBlur = glow;
    ctx.fillStyle = '#1a1020'; ctx.beginPath(); ctx.arc(0, 0, b.r, 0, 7); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#2a1730'; ctx.beginPath(); ctx.arc(0, 0, b.r - 5, 0, 7); ctx.fill();
    ctx.save(); ctx.rotate(b.angle);
    ctx.fillStyle = '#ffd23f'; ctx.beginPath(); ctx.moveTo(b.r - 4, -6); ctx.lineTo(b.r + 8, 0); ctx.lineTo(b.r - 4, 6); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#ff2d55'; ctx.beginPath(); ctx.arc(b.r - 12, -6, 3.4, 0, 7); ctx.arc(b.r - 12, 6, 3.4, 0, 7); ctx.fill();
    ctx.restore();
    ctx.restore();
    ctx.font = '800 13px Fredoka, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.strokeText('THE MANAGER', b.x, b.y - b.r - 8);
    ctx.fillStyle = '#ff2d55'; ctx.fillText('THE MANAGER', b.x, b.y - b.r - 8);
  }

  _drawTrail(ctx) {
    for (const t of this.trailPts) {
      const k = Math.max(0, t.life / 0.5);
      ctx.globalAlpha = k * 0.6;
      ctx.fillStyle = t.color;
      ctx.beginPath(); ctx.arc(t.x, t.y, 8 * k + 3, 0, 7); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  _drawPeels(ctx) {
    ctx.font = '24px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const peel of this.peels) { ctx.globalAlpha = Math.min(1, peel.life); ctx.fillText('🍌', peel.x, peel.y); }
    ctx.globalAlpha = 1;
  }

  _drawProjectiles(ctx) {
    ctx.font = '20px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const pr of this.projectiles) ctx.fillText(SNACKS[pr.type].emoji, pr.x, pr.y);
  }

  _drawDecoy(ctx) {
    if (!this.decoy) return;
    const d = this.decoy;
    ctx.strokeStyle = 'rgba(255,45,85,0.45)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(d.x, d.y, 66 + Math.sin(d.pulse) * 6, 0, 7); ctx.stroke();
    ctx.globalAlpha = 0.65;
    this._drawAvatar(ctx, d.x, d.y, 18, this.profile.color, this.profile.hat, 0, this.profile.name, false, 0, false);
    ctx.globalAlpha = 1;
  }

  _drawPet(ctx) {
    if (!this.pet) return;
    const pet = this.pet, info = PETS[pet.id];
    const bob = Math.sin(pet.bob) * 4;
    ctx.save(); ctx.translate(pet.x, pet.y + bob);
    ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.beginPath(); ctx.ellipse(0, 12, 12, 4, 0, 0, 7); ctx.fill();
    if (info && info.color) { ctx.fillStyle = info.color; ctx.beginPath(); ctx.arc(0, 0, 13, 0, 7); ctx.fill(); ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = 2; ctx.stroke(); }
    ctx.font = '18px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(info ? info.icon : '🐾', 0, 0);
    ctx.restore();
  }

  _drawAvatar(ctx, x, y, r, color, hat, angle, name, isSelf, stun, invis, opts = {}) {
    const tm = performance.now() / 1000;
    const bob = opts.bob || 0;
    const sqx = opts.squash ? opts.squash.x : 1;
    const sqy = opts.squash ? opts.squash.y : 1;
    ctx.save();
    ctx.globalAlpha = invis ? 0.4 : 1;
    // contact shadow
    ctx.fillStyle = 'rgba(0,0,0,0.32)';
    ctx.beginPath(); ctx.ellipse(x, y + r - 1, r * (1 - bob * 0.015), 7, 0, 0, 7); ctx.fill();
    ctx.save();
    ctx.translate(x, y - bob);
    ctx.scale(sqx, sqy);
    // body (skin or 3D-shaded color)
    ctx.save();
    ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.clip();
    if (opts.skin && opts.skin !== 'none') this._drawSkinBody(ctx, opts.skin, r, tm);
    else {
      const base = stun > 0 ? '#ff9a9a' : color;
      const g = ctx.createRadialGradient(-r * 0.35, -r * 0.42, r * 0.15, 0, 0, r * 1.15);
      g.addColorStop(0, this._lighten(base, 65));
      g.addColorStop(0.55, base);
      g.addColorStop(1, this._lighten(base, -50));
      ctx.fillStyle = g; ctx.fillRect(-r, -r, r * 2, r * 2);
    }
    ctx.restore();
    ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.stroke();
    // glossy highlight
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.beginPath(); ctx.ellipse(-r * 0.3, -r * 0.45, r * 0.5, r * 0.26, -0.5, 0, 7); ctx.fill();
    // eyes + punch fist
    ctx.save(); ctx.rotate(angle);
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(r - 6, -5, 3.6, 0, 7); ctx.arc(r - 6, 5, 3.6, 0, 7); ctx.fill();
    ctx.fillStyle = '#111'; ctx.beginPath(); ctx.arc(r - 4.5, -5, 1.9, 0, 7); ctx.arc(r - 4.5, 5, 1.9, 0, 7); ctx.fill();
    if (opts.swing) {
      const fx = r + 3 + Math.sin(opts.swing * Math.PI) * 14;
      ctx.fillStyle = this._lighten(color, -8);
      ctx.beginPath(); ctx.arc(fx, 0, r * 0.32, 0, 7); ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = 2; ctx.stroke();
    }
    ctx.restore();
    this._drawHat(ctx, hat, r);
    ctx.restore();
    // name
    ctx.font = '700 13px Fredoka, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.strokeText(name, x, y - r - 8);
    ctx.fillStyle = isSelf ? '#ffd23f' : '#fff';
    ctx.fillText(name, x, y - r - 8);
    ctx.restore();
  }

  _drawSkinBody(ctx, skin, r, t) {
    const D = r * 2;
    if (skin === 'golden') {
      const a = Math.sin(t * 2) * 0.5 + 0.5;
      const g = ctx.createLinearGradient(-r, -r, r, r);
      g.addColorStop(0, '#fff6c0'); g.addColorStop(0.35 + a * 0.25, '#ffd23f'); g.addColorStop(1, '#b8860b');
      ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
      ctx.save(); ctx.rotate(t * 1.2); ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fillRect(-r, -2, D, 3); ctx.restore();
    } else if (skin === 'galaxy') {
      ctx.fillStyle = '#140b2e'; ctx.fillRect(-r, -r, D, D);
      const gg = ctx.createRadialGradient(0, 0, 2, 0, 0, r); gg.addColorStop(0, 'rgba(176,107,255,0.6)'); gg.addColorStop(1, 'rgba(20,11,46,0)'); ctx.fillStyle = gg; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 16; i++) { const ang = i * 2.39 + t * 0.5; const rad = ((i % 5) / 5) * r * 0.9; const x = Math.cos(ang) * rad, y = Math.sin(ang) * rad; const tw = 0.4 + 0.6 * Math.abs(Math.sin(t * 3 + i)); ctx.fillStyle = `rgba(255,255,255,${tw})`; ctx.beginPath(); ctx.arc(x, y, 1.3, 0, 7); ctx.fill(); }
    } else if (skin === 'rainbow') {
      const hue = (t * 90) % 360; const g = ctx.createLinearGradient(-r, -r, r, r); g.addColorStop(0, `hsl(${hue},90%,65%)`); g.addColorStop(1, `hsl(${(hue + 120) % 360},90%,55%)`); ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
    } else if (skin === 'lava') {
      ctx.fillStyle = '#2a0f08'; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 5; i++) { const fl = 0.5 + 0.5 * Math.sin(t * 4 + i * 1.3); ctx.strokeStyle = `rgba(255,${90 + fl * 110},30,${0.6 + fl * 0.4})`; ctx.lineWidth = 2 + fl * 2; ctx.beginPath(); ctx.moveTo(-r + i * (D / 5), -r); ctx.lineTo(-r + i * (D / 5) + (fl * 10 - 5), r); ctx.stroke(); }
      const gg = ctx.createRadialGradient(0, 0, 2, 0, 0, r); gg.addColorStop(0, 'rgba(255,180,60,0.55)'); gg.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = gg; ctx.fillRect(-r, -r, D, D);
    } else if (skin === 'diamond') {
      const a = Math.sin(t * 3) * 0.5 + 0.5; const g = ctx.createLinearGradient(-r, -r, r, r); g.addColorStop(0, '#d8f6ff'); g.addColorStop(0.4 + a * 0.2, '#7fd8ff'); g.addColorStop(1, '#4a90d0'); ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
      ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(-r, 0); ctx.lineTo(0, -r); ctx.lineTo(r, 0); ctx.lineTo(0, r); ctx.closePath(); ctx.stroke();
    } else if (skin === 'ghost') {
      const a = 0.55 + 0.2 * Math.sin(t * 2); ctx.fillStyle = `rgba(230,235,255,${a})`; ctx.fillRect(-r, -r, D, D);
    } else if (skin === 'neon') {
      ctx.fillStyle = '#0e1230'; ctx.fillRect(-r, -r, D, D);
      const p = 0.5 + 0.5 * Math.sin(t * 4); ctx.strokeStyle = `rgba(46,230,214,${0.6 + p * 0.4})`; ctx.lineWidth = 3 + p * 2; ctx.beginPath(); ctx.arc(0, 0, r - 3, 0, 7); ctx.stroke();
    } else if (skin === 'aurora') {
      const hue = (t * 40) % 360; const g = ctx.createLinearGradient(-r, -r, r, r);
      g.addColorStop(0, `hsl(${140 + Math.sin(t) * 20},80%,60%)`); g.addColorStop(0.5, `hsl(${210 + Math.sin(t * 1.3) * 20},80%,62%)`); g.addColorStop(1, `hsl(${280 + Math.sin(t * 0.7) * 20},75%,62%)`);
      ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 3; i++) { ctx.strokeStyle = `hsla(${(hue + i * 90) % 360},90%,80%,0.5)`; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-r, Math.sin(t * 2 + i) * r * 0.5); ctx.quadraticCurveTo(0, Math.sin(t * 2 + i + 1) * r, r, Math.sin(t * 2 + i + 2) * r * 0.5); ctx.stroke(); }
    } else if (skin === 'inferno') {
      ctx.fillStyle = '#1a0503'; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 7; i++) { const fl = 0.5 + 0.5 * Math.sin(t * 6 + i * 0.9); const yy = r - (t * 40 + i * 14) % (D); ctx.fillStyle = `rgba(255,${60 + fl * 150},20,${0.5 + fl * 0.4})`; ctx.beginPath(); ctx.arc((i - 3) * 5, yy, 3 + fl * 3, 0, 7); ctx.fill(); }
      const gg = ctx.createRadialGradient(0, r, 2, 0, r, r * 1.6); gg.addColorStop(0, 'rgba(255,90,20,0.7)'); gg.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = gg; ctx.fillRect(-r, -r, D, D);
    } else if (skin === 'plasma') {
      const g = ctx.createRadialGradient(Math.cos(t * 2) * 4, Math.sin(t * 2) * 4, 1, 0, 0, r);
      g.addColorStop(0, '#ffffff'); g.addColorStop(0.4, '#ff2a85'); g.addColorStop(1, '#4d8bff'); ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 4; i++) { const a = t * 3 + i * 1.57; ctx.strokeStyle = `rgba(255,255,255,${0.4 + 0.4 * Math.sin(t * 5 + i)})`; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); ctx.stroke(); }
    } else if (skin === 'cyber') {
      ctx.fillStyle = '#06131a'; ctx.fillRect(-r, -r, D, D);
      ctx.strokeStyle = `rgba(46,230,214,0.8)`; ctx.lineWidth = 1;
      for (let i = -r; i <= r; i += 5) { ctx.globalAlpha = 0.3 + 0.3 * Math.sin(t * 4 + i); ctx.beginPath(); ctx.moveTo(-r, i); ctx.lineTo(r, i); ctx.stroke(); }
      ctx.globalAlpha = 1; const p = 0.5 + 0.5 * Math.sin(t * 6); ctx.strokeStyle = `rgba(166,255,0,${0.6 + p * 0.4})`; ctx.lineWidth = 2 + p * 2; ctx.strokeRect(-r + 2, -r + 2, D - 4, D - 4);
    } else if (skin === 'void') {
      ctx.fillStyle = '#05010f'; ctx.fillRect(-r, -r, D, D);
      const gg = ctx.createRadialGradient(0, 0, 1, 0, 0, r); gg.addColorStop(0, 'rgba(0,0,0,1)'); gg.addColorStop(0.7, 'rgba(125,63,214,0.5)'); gg.addColorStop(1, 'rgba(255,42,133,0.7)'); ctx.fillStyle = gg; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 10; i++) { const a = i * 0.9 - t * 2; const rad = ((i % 4) / 4) * r; ctx.fillStyle = `rgba(255,255,255,${0.3 + 0.3 * Math.sin(t * 3 + i)})`; ctx.beginPath(); ctx.arc(Math.cos(a) * rad, Math.sin(a) * rad, 1, 0, 7); ctx.fill(); }
    } else if (skin === 'frost') {
      const g = ctx.createLinearGradient(-r, -r, r, r); g.addColorStop(0, '#cdeeff'); g.addColorStop(1, '#4d8bff'); ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
      ctx.strokeStyle = `rgba(255,255,255,${0.5 + 0.3 * Math.sin(t * 3)})`; ctx.lineWidth = 1.5;
      for (let i = 0; i < 6; i++) { const a = i * 1.05 + t * 0.6; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); ctx.stroke(); }
      for (let i = 0; i < 5; i++) { const yy = ((t * 20 + i * 12) % D) - r; ctx.fillStyle = 'rgba(255,255,255,0.8)'; ctx.beginPath(); ctx.arc(Math.sin(t + i) * r * 0.6, yy, 1.4, 0, 7); ctx.fill(); }
    } else if (skin === 'storm') {
      ctx.fillStyle = '#20263b'; ctx.fillRect(-r, -r, D, D);
      const flash = Math.max(0, Math.sin(t * 9) - 0.7) * 3; ctx.fillStyle = `rgba(255,240,150,${flash})`; ctx.fillRect(-r, -r, D, D);
      ctx.strokeStyle = '#ffd23f'; ctx.lineWidth = 2; ctx.beginPath();
      let bx = -r * 0.3, by = -r; ctx.moveTo(bx, by);
      for (let i = 0; i < 4; i++) { bx += (Math.sin(t * 7 + i) * r * 0.5); by += D / 4; ctx.lineTo(Math.max(-r, Math.min(r, bx)), by); }
      ctx.globalAlpha = 0.5 + 0.5 * Math.sin(t * 8); ctx.stroke(); ctx.globalAlpha = 1;
    } else if (skin === 'prism') {
      for (let i = 0; i < 6; i++) { const a0 = (i / 6) * Math.PI * 2 + t * 0.8; ctx.fillStyle = `hsl(${(i * 60 + t * 40) % 360},90%,62%)`; ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, r, a0, a0 + Math.PI / 3); ctx.closePath(); ctx.fill(); }
      ctx.fillStyle = `rgba(255,255,255,${0.6 + 0.3 * Math.sin(t * 4)})`; ctx.beginPath(); ctx.arc(0, 0, r * 0.35, 0, 7); ctx.fill();
    } else if (skin === 'nebula') {
      const g = ctx.createRadialGradient(0, 0, 1, 0, 0, r); g.addColorStop(0, '#ffffff'); g.addColorStop(0.4, '#b06bff'); g.addColorStop(0.75, '#4d2b9e'); g.addColorStop(1, '#100628'); ctx.fillStyle = g; ctx.fillRect(-r, -r, D, D);
      for (let i = 0; i < 14; i++) { const a = i * 0.7 + t * 1.2; const rad = ((i * 37 % 100) / 100) * r; const tw = 0.4 + 0.6 * Math.sin(t * 4 + i); ctx.fillStyle = `rgba(255,255,255,${tw})`; ctx.beginPath(); ctx.arc(Math.cos(a) * rad, Math.sin(a) * rad, i % 3 === 0 ? 1.8 : 1, 0, 7); ctx.fill(); }
    } else { ctx.fillStyle = '#888'; ctx.fillRect(-r, -r, D, D); }
  }

  _drawHat(ctx, hat, r) {
    if (hat === 'cap') { ctx.fillStyle = '#e23b3b'; this._rr(ctx, -r, -r - 6, r * 2, 8, 4); ctx.fill(); ctx.fillRect(r - 2, -r - 4, 8, 5); }
    else if (hat === 'crown') { ctx.fillStyle = '#ffd23f'; ctx.beginPath(); ctx.moveTo(-r + 2, -r); ctx.lineTo(-r + 2, -r - 10); ctx.lineTo(-r / 2, -r - 2); ctx.lineTo(0, -r - 12); ctx.lineTo(r / 2, -r - 2); ctx.lineTo(r - 2, -r - 10); ctx.lineTo(r - 2, -r); ctx.closePath(); ctx.fill(); }
    else if (hat === 'beanie') { ctx.fillStyle = '#4d8bff'; this._rr(ctx, -r, -r - 8, r * 2, 12, 6); ctx.fill(); ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(0, -r - 8, 4, 0, 7); ctx.fill(); }
    else if (hat === 'party') { ctx.fillStyle = '#ff6fb5'; ctx.beginPath(); ctx.moveTo(0, -r - 18); ctx.lineTo(-9, -r); ctx.lineTo(9, -r); ctx.closePath(); ctx.fill(); ctx.fillStyle = '#ffd23f'; ctx.beginPath(); ctx.arc(0, -r - 18, 3, 0, 7); ctx.fill(); }
    else if (hat === 'halo') { ctx.strokeStyle = '#ffd23f'; ctx.lineWidth = 4; ctx.shadowColor = '#ffd23f'; ctx.shadowBlur = 10; ctx.beginPath(); ctx.ellipse(0, -r - 11, r * 0.7, 5, 0, 0, 7); ctx.stroke(); ctx.shadowBlur = 0; }
    else if (hat === 'ninja') { ctx.fillStyle = '#e23b3b'; this._rr(ctx, -r, -r + 1, r * 2, 8, 3); ctx.fill(); ctx.beginPath(); ctx.moveTo(r - 2, -r + 1); ctx.lineTo(r + 14, -r - 3); ctx.lineTo(r + 12, -r + 9); ctx.closePath(); ctx.fill(); }
    else {
      const em = { top: '🎩', chef: '🧑‍🍳', wizard: '🧙', alien: '👽', devil: '😈', cowboy: '🤠', grad: '🎓', flower: '🌸', star: '⭐', skull: '💀', viking: '⛑️', unicorn: '🦄', robot: '🤖', pumpkin: '🎃', gift: '🎁', headphones: '🎧', sprout: '🌱', firehat: '🔥' }[hat];
      if (em) { ctx.font = Math.round(r * 1.7) + 'px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillText(em, 0, -r + 6); }
    }
  }

  _drawRemotePlayers(ctx) {
    const c = this._cam, W = this.canvas.width, H = this.canvas.height;
    for (const p of this.remote) {
      if (p.x < c.x - 60 || p.x > c.x + W + 60 || p.y < c.y - 60 || p.y > c.y + H + 60) continue;
      this._drawAvatar(ctx, p.x, p.y, 18, p.skin || '#4d8bff', p.hat || 'none', 0, (p.isBot ? '🤖 ' : '') + p.name, false, 0, false);
    }
  }

  _drawPlayer(ctx) {
    const p = this.player;
    if (p.stun > 0 && Math.floor(p.stun * 10) % 2 === 0) return; // blink when caught
    const aura = AURAS[this.profile.aura];
    if (aura && aura.color) {
      const pu = performance.now() / 300;
      ctx.save();
      ctx.globalAlpha = 0.55 + Math.sin(pu) * 0.2;
      ctx.shadowColor = aura.color; ctx.shadowBlur = 22;
      ctx.strokeStyle = aura.color; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r + 10 + Math.sin(pu) * 2, 0, 7); ctx.stroke();
      ctx.restore();
    }
    this._drawAvatar(ctx, p.x, p.y, p.r, this.profile.color, this.profile.hat, p.angle, this.profile.name, true, p.stun, this._powActive('invisible'), this._playerAnim());
    // active power ring
    if (this._powActive('shield')) { ctx.strokeStyle = 'rgba(77,139,255,0.8)'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(p.x, p.y, p.r + 8, 0, 7); ctx.stroke(); }
    if (this._powActive('magnet')) { ctx.strokeStyle = 'rgba(255,138,61,0.4)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, 190, 0, 7); ctx.stroke(); }
  }

  _playerAnim() {
    const p = this.player;
    const t = performance.now() / 1000;
    const sp = Math.hypot(p.vx, p.vy);
    const bob = sp > 40 ? Math.abs(Math.sin(t * 12)) * 4 : Math.sin(t * 2.5) * 1.2;
    const stretch = clamp(sp / 700, 0, 0.16);
    return { skin: this.profile.skin, bob, squash: { x: 1 - stretch * 0.5, y: 1 + stretch * 0.5 }, swing: Math.max(0, p.swing) };
  }

  _drawPowerups(ctx) {
    for (const pu of this.powerups) {
      if (!pu.active) continue;
      const info = POWERUPS[pu.type];
      const bob = Math.sin(pu.bob) * 5;
      ctx.save(); ctx.translate(pu.x, pu.y + bob);
      ctx.shadowColor = info.color; ctx.shadowBlur = 18;
      ctx.fillStyle = info.color + '33'; ctx.beginPath(); ctx.arc(0, 0, 22, 0, 7); ctx.fill();
      ctx.strokeStyle = info.color; ctx.lineWidth = 2.5; ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.font = '22px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(info.emoji, 0, 1);
      ctx.restore();
    }
  }

  _drawNoises(ctx) {
    for (const n of this.noises) {
      const a = (1 - n.age / 0.6) * 0.4;
      ctx.strokeStyle = `rgba(255,90,95,${a})`; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, 7); ctx.stroke();
    }
  }

  _drawParticles(ctx) {
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 7); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  _drawFloaters(ctx) {
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const f of this.floaters) {
      ctx.globalAlpha = Math.max(0, Math.min(1, f.life));
      ctx.font = '800 18px Fredoka, sans-serif';
      ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.strokeText(f.text, f.x, f.y);
      ctx.fillStyle = f.color; ctx.fillText(f.text, f.x, f.y);
    }
    ctx.globalAlpha = 1;
  }

  _drawMinimap(ctx, W, H) {
    const mw = 170, mh = mw * (WORLD.h / WORLD.w);
    const mx = W - mw - 16, my = H - mh - 16;
    const sx = mw / WORLD.w, sy = mh / WORLD.h;
    ctx.fillStyle = 'rgba(15,18,34,0.8)'; this._rr(ctx, mx - 4, my - 4, mw + 8, mh + 8, 8); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1; ctx.stroke();
    ctx.save();
    this._rr(ctx, mx, my, mw, mh, 6); ctx.clip();
    ctx.fillStyle = 'rgba(60,68,104,0.5)'; ctx.fillRect(mx, my, mw, mh);
    for (const b of this.buildings) { ctx.fillStyle = 'rgba(120,130,170,0.6)'; ctx.fillRect(mx + b.x * sx, my + b.y * sy, b.w * sx, b.h * sy); }
    for (const m of this.machines) { ctx.fillStyle = m.broken ? '#555' : MACHINE_TYPES[m.type].color; ctx.fillRect(mx + m.x * sx - 2, my + m.y * sy - 2, 4, 4); }
    for (const pu of this.powerups) { if (!pu.active) continue; ctx.fillStyle = POWERUPS[pu.type].color; ctx.fillRect(mx + pu.x * sx - 1.5, my + pu.y * sy - 1.5, 3, 3); }
    for (const g of this.guards) { ctx.fillStyle = g.state === 'chase' ? '#ff2d55' : '#7ac8ff'; ctx.beginPath(); ctx.arc(mx + g.x * sx, my + g.y * sy, 2.5, 0, 7); ctx.fill(); }
    for (const p of this.remote) { ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.beginPath(); ctx.arc(mx + p.x * sx, my + p.y * sy, 1.8, 0, 7); ctx.fill(); }
    ctx.fillStyle = '#ffd23f'; ctx.beginPath(); ctx.arc(mx + this.player.x * sx, my + this.player.y * sy, 3.2, 0, 7); ctx.fill();
    if (this.boss) { ctx.fillStyle = '#ff2d55'; ctx.beginPath(); ctx.arc(mx + this.boss.x * sx, my + this.boss.y * sy, 3.2, 0, 7); ctx.fill(); }
    ctx.restore();
  }
}
